import { formatValue, lengthLimit, showLoader, printWorkAround, helpText } from '../data-viz-helpers.js'

customElements.define('zero-percent-of', class extends HTMLElement {
    static observedAttributes = ['data-value', 'data-total', 'data-format', 'data-labels', 'data-bg', 'data-fill', 'data-heading', 'data-help']

    constructor() {
        super().attachShadow({mode: 'open'})

        this.height = 116
        this.width = 336
    }

    connectedCallback() {
        this.getProps()
        this.shadowRoot.innerHTML = showLoader([this.dataset.value, this.dataset.total], this.render())
    }

    attributeChangedCallback(_, oldVal, newVal) {
        if(oldVal !== newVal) {
            this.connectedCallback()
        }
    }

    getProps() {
        this.bg = this.dataset.bg ?? 'var(--color-4-lt)'
        this.fill = this.dataset.fill ?? 'var(--color-4-dk)'
        this.heading = this.dataset.heading ?? 'xxxxxxxxx'
        this.values = (this.hasAttribute('data-value') && this.hasAttribute('data-total')) ? [this.dataset.value, this.dataset.total] : [0,0]
        this.percentage = 0
        this.format = this.dataset.format ?? 'decimal'
    }

    createHeading() {
        return `
            <p class="heading">${lengthLimit(this.heading, 22)}</p>
        `
    }

    createMeter() {
        const [currentVal, potentialVal] = this.values
        const margin = 6
        const x = margin / 2
        const y = margin / 2
        const height = this.height - margin
        const width = this.width - margin
        const strokeWidth = 6
        const ry = height / 2
        const percentage = currentVal / potentialVal
        const fillWidth = width * percentage
        const minFillWidth = this.width / 2
        const fillGreaterThanMin = fillWidth >= minFillWidth
        const labelX = (fillGreaterThanMin) ? margin + (fillWidth / 2) : fillWidth + 20 
        const labelFill = (fillGreaterThanMin) ? 'white' : this.fill
        const textAnchor = (fillGreaterThanMin) ? 'middle' : 'left'

        return `
            <defs>
                <clipPath id="clip">
                    <rect 
                        x="${x}" 
                        y="${y}" 
                        ry="${ry}" 
                        width="${width}" 
                        height="${height}" />
                </clipPath>
            </defs>

            <!-- Empty BG -->
            <rect 
                x="${x}" 
                y="${y}" 
                ry="${ry}" 
                width="${width}" 
                height="${height}" 
                fill="white" />

            <!-- Percentage Fill -->
            <rect 
                id="fill"
                clip-path="url(#clip)"
                x="${x + (strokeWidth / 2)}" 
                y="${y + (strokeWidth / 2)}" 
                ry="10" 
                width="${fillWidth}" 
                height="${height - strokeWidth}" 
                fill="${this.fill}" />

            <!-- Border -->
            <rect 
                x="${x}" 
                y="${y}" 
                ry="${ry}" 
                width="${width}" 
                height="${height}" 
                stroke="white"
                stroke-width="${strokeWidth}"
                fill="transparent" />

            <!-- % Value -->
             <text 
                text-anchor="${textAnchor}" 
                alignment-baseline="middle"
                x="${labelX}" 
                y="${y + (height / 2) + 3}" 
                width="${fillWidth}"
                fill="${labelFill}" 
                style="font-size: 34px; font-weight: 900;">${formatValue(percentage, 'percent')}</text>
        `
    }

    createLabel() {
        const [currentVal, potentialVal] = this.values

        return `
            <p class="label">${formatValue(currentVal, this.format)} of ${formatValue(potentialVal, this.format)}</p>
        `
    }

    render() {
        return `
            <style>
                * {
                    box-sizing: border-box;
                }

                :host {
                    background-color: ${this.bg};
                    border-radius: var(--chart-radius);
                    container-type: inline-size;
                    display: grid;
                    gap: 1em;
                    height: 100%;
                    overflow: clip;
                    padding: 2rem;
                    place-content: center;
                    position: relative;
                    -webkit-user-select: none;
                    user-select: none;
                }

                svg {
                    display: block;
                    width: 100%;
                }

                p {
                    margin: 0;
                }

                .heading {
                    color: ${this.fill};
                    font-size: 7cqw;
                    font-weight: 800;
                    text-align: center;
                }

                .label {
                    color: ${this.fill};
                    font-size: 6cqw;
                    font-weight: 600;
                    text-align: center;
                }
            </style>

            ${printWorkAround(this)}

            ${this.createHeading()}

            <svg 
                xmlns="http://www.w3.org/2000/svg" 
                version="1.1" 
                viewBox="0 0 ${this.width} ${this.height}">
                <rect fill="${this.bg}" x="0" y="0" width="${this.width}" height="${this.height}"></rect>
                ${this.createMeter()}
            </svg>

            ${this.createLabel()}

            ${helpText(this)}
        `
    }
})